import { useEffect, useMemo, useState } from "react";
import { PaginationBar } from "@/components/common/PaginationBar";
import { usePagination } from "@/components/common/usePagination";
import { ManufacturingEntityBulkBar } from "@/components/manufacturing/ManufacturingEntityBulkBar";
import {
  ghostBtnClass,
  mfgInlineInputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { downloadCsv } from "@/lib/manufacturing/inventoryCsvExport";
import { slotLabelLinesToCsv } from "@/lib/manufacturing/slotLabelsCsvExport";
import {
  listManufacturingSlotCleanupLines,
  listManufacturingSlotLabelInventory,
  renameManufacturingSlotLabel,
  type SlotLabelUsage,
} from "@/lib/manufacturing/slotLabelCleanupClient";
import { normalizeSlotLabel, sanitizeSlotLabelInput } from "@/lib/manufacturing/slotLabel";

export function ManufacturingSlotLabelsRenamePanel({
  enabled,
  refreshTick,
  onRenamed,
  onShowAssociatedRecipes,
}: {
  enabled: boolean;
  refreshTick: number;
  onRenamed?: () => void;
  onShowAssociatedRecipes?: (label: string) => void;
}) {
  const [labels, setLabels] = useState<SlotLabelUsage[]>([]);
  const [busy, setBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [renameTarget, setRenameTarget] = useState<string | null>(null);
  const [toLabel, setToLabel] = useState("");

  async function reload() {
    if (!enabled) return;
    setBusy(true);
    setError(null);
    const r = await listManufacturingSlotLabelInventory();
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setLabels(r.data.labels);
    setSelectedLabels((prev) => prev.filter((l) => r.data.labels.some((row) => row.label === l)));
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, refreshTick]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return labels;
    return labels.filter((row) => row.label.toLowerCase().includes(q));
  }, [labels, search]);

  const pager = usePagination(filtered, 10);
  const renameUsage = renameTarget
    ? labels.find((row) => row.label === renameTarget) ?? null
    : null;

  useEffect(() => {
    pager.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  function toggleLabel(label: string) {
    setSelectedLabels((prev) =>
      prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label],
    );
  }

  function pickRename(label: string) {
    setRenameTarget(label);
    setToLabel(label);
    setMessage(null);
  }

  function selectAllFiltered() {
    setSelectedLabels(filtered.map((row) => row.label));
  }

  async function exportSelectedLabels() {
    if (selectedLabels.length === 0) return;
    setExportBusy(true);
    setError(null);
    const r = await listManufacturingSlotCleanupLines("all");
    setExportBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    const labelSet = new Set(selectedLabels);
    const lines = r.data.lines.filter((ln) => ln.slot_label && labelSet.has(ln.slot_label));
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`mfg-slot-labels-${stamp}.csv`, slotLabelLinesToCsv(lines));
  }

  async function runRename(from: string, to: string) {
    setSaveBusy(true);
    setError(null);
    setMessage(null);
    const r = await renameManufacturingSlotLabel({
      from_label: from,
      to_label: to,
    });
    setSaveBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setMessage(
      `Updated ${r.data.lines_updated} line${r.data.lines_updated === 1 ? "" : "s"}` +
        (r.data.attachments_updated
          ? ` and ${r.data.attachments_updated} attachment${
              r.data.attachments_updated === 1 ? "" : "s"
            }`
          : ""),
    );
    setRenameTarget(null);
    setToLabel("");
    await reload();
    onRenamed?.();
  }

  async function rename() {
    if (!renameTarget || !toLabel.trim()) return;
    await runRename(renameTarget, toLabel.trim());
  }

  function confirmReassign(targetLabel: string) {
    if (!renameTarget || renameTarget === targetLabel) return;
    const count = renameUsage?.line_count ?? 0;
    const ok = window.confirm(
      `Are you sure you want to reassign all ${count} labeled material${
        count === 1 ? "" : "s"
      } from "${renameTarget}" to "${targetLabel}"?`,
    );
    if (!ok) return;
    void runRename(renameTarget, targetLabel);
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-600 dark:text-zinc-400">
        Search labels, export lines by label, or rename a label across recipe lines (and attachment
        slot replacements).
      </p>
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/35 dark:text-red-200">
          {error}
        </div>
      ) : null}
      {message ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/35 dark:text-emerald-100">
          {message}
        </div>
      ) : null}
      <input
        className={mfgInlineInputClass}
        placeholder="Search labels (partial match)…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <ManufacturingEntityBulkBar
        entityLabel="label"
        selectedCount={selectedLabels.length}
        filteredCount={filtered.length}
        allFilteredSelected={
          filtered.length > 0 && filtered.every((row) => selectedLabels.includes(row.label))
        }
        busy={exportBusy}
        onSelectAllFiltered={selectAllFiltered}
        onClear={() => setSelectedLabels([])}
        onExportCsv={() => void exportSelectedLabels()}
        hint="Export includes all BOM lines currently using the selected slot labels."
      />

      <div className="max-h-72 space-y-1 overflow-y-auto rounded-lg border border-gray-200 p-2 dark:border-zinc-700">
        {busy ? (
          <div className="py-4 text-center text-sm text-gray-500">Loading…</div>
        ) : null}
        {!busy &&
          pager.paginatedItems.map((row) => {
            const active = renameTarget === row.label;
            const checked = selectedLabels.includes(row.label);
            return (
              <div
                key={row.label}
                className={
                  "flex items-center gap-2 rounded-md px-2 py-1.5 " +
                  (active
                    ? "bg-white shadow-sm dark:bg-zinc-900"
                    : "hover:bg-white/70 dark:hover:bg-zinc-900/60")
                }
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleLabel(row.label)}
                  aria-label={`Select ${row.label}`}
                />
                <button
                  type="button"
                  onClick={() => pickRename(row.label)}
                  className="min-w-0 flex-1 text-left text-sm"
                >
                  <span className="font-medium">{row.label}</span>
                  <span className="ml-2 text-xs text-gray-500">
                    {row.line_count} line{row.line_count === 1 ? "" : "s"}
                    {row.attachment_count > 0 ? ` · ${row.attachment_count} att.` : ""}
                  </span>
                </button>
                {renameTarget && !active ? (
                  <button
                    type="button"
                    className={ghostBtnClass}
                    disabled={saveBusy}
                    onClick={() => confirmReassign(row.label)}
                  >
                    Use this Label
                  </button>
                ) : null}
              </div>
            );
          })}
        {!busy && pager.paginatedItems.length === 0 ? (
          <div className="py-4 text-center text-sm text-gray-500">No matching labels.</div>
        ) : null}
      </div>
      <PaginationBar
        pageIndex={pager.pageIndex}
        pageCount={pager.pageCount}
        pageSize={pager.pageSize}
        totalCount={pager.totalCount}
        firstShown={pager.firstShown}
        lastShown={pager.lastShown}
        onPageChange={pager.setPageIndex}
        onPageSizeChange={pager.setPageSize}
        noun="labels"
        alwaysShow
      />
      {renameTarget ? (
        <div className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-zinc-700">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex min-w-[10rem] flex-1 items-center self-stretch">
              <p className="text-base font-semibold leading-snug text-gray-900 dark:text-zinc-100">
                <span className="text-emerald-600 dark:text-emerald-400">Renaming</span>{" "}
                <span className="font-bold">{renameTarget}</span>
              </p>
            </div>
            <label className="flex min-w-[180px] flex-1 flex-col gap-1.5">
              <span className="text-xs font-semibold text-gray-700 dark:text-zinc-300">
                New label
              </span>
              <input
                className={mfgInlineInputClass}
                value={toLabel}
                onChange={(e) => setToLabel(sanitizeSlotLabelInput(e.target.value))}
                onBlur={() => setToLabel((prev) => normalizeSlotLabel(prev))}
              />
            </label>
            <div className="flex flex-wrap items-center gap-2 self-end">
              <button
                type="button"
                className={mfgPrimaryBtnClass}
                disabled={saveBusy || !toLabel.trim() || toLabel.trim() === renameTarget}
                onClick={() => void rename()}
              >
                {saveBusy ? "Saving…" : "Apply rename"}
              </button>
              <button
                type="button"
                className={ghostBtnClass}
                disabled={saveBusy}
                onClick={() => {
                  setRenameTarget(null);
                  setToLabel("");
                }}
              >
                Cancel
              </button>
            </div>
          </div>
          <button
            type="button"
            className={ghostBtnClass}
            disabled={saveBusy}
            onClick={() => onShowAssociatedRecipes?.(renameTarget)}
          >
            Show recipes using “{renameTarget}”
          </button>
        </div>
      ) : null}
    </div>
  );
}
